import React, { useEffect, useState } from "react";
import { useNoIndex } from "../lib/usePageMeta";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { StatusBadge } from "../components/product/StatusBadge";
import { ArrowRight, Package, ShieldCheck, UserCircle, Truck, Headset } from "@phosphor-icons/react";
import { formatPrice } from "../lib/format";

const VSTATUS = {
  unverified: { label: "Unverified", cls: "border-slate-500/40 text-brand-text bg-slate-500/5" },
  pending: { label: "Pending Verification", cls: "border-amber-500/40 text-amber-300 bg-amber-500/5" },
  approved: { label: "Approved", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
  declined: { label: "Declined", cls: "border-red-500/40 text-red-300 bg-red-500/5" },
  expired: { label: "Expired", cls: "border-slate-500/40 text-brand-text bg-slate-500/5" },
};

export default function AccountDashboard() {
  useNoIndex("/account");
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="account-guard">
        <div className="font-display text-2xl text-brand-navy">Please sign in</div>
        <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-brand-accent">Go to sign in <ArrowRight size={16} weight="bold" /></Link>
      </div>
    );
  }

  const v = VSTATUS[user.verification_status] || VSTATUS.unverified;

  return (
    <div className="max-w-6xl mx-auto px-4 py-14" data-testid="account-dashboard" data-clarity-mask="true">
      <div className="overline mb-3">Account</div>
      <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">
        Welcome, {user.full_name}
      </h1>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-surface border border-brand-border">
        <div className="bg-white p-6">
          <div className="overline mb-2 flex items-center gap-2"><UserCircle size={14} weight="duotone" className="text-brand-accent" /> Account type</div>
          <div className="font-mono text-sm text-brand-navy uppercase tracking-wide">{user.account_type.replace(/_/g, " ")}</div>
        </div>
        <div className="bg-white p-6">
          <div className="overline mb-2 flex items-center gap-2"><ShieldCheck size={14} weight="duotone" className="text-brand-accent" /> Verification</div>
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${v.cls}`}>{v.label}</span>
        </div>
        <div className="bg-white p-6">
          <div className="overline mb-2 flex items-center gap-2"><Package size={14} weight="duotone" className="text-brand-accent" /> Orders</div>
          <div className="font-mono text-2xl text-brand-navy">{orders.length}</div>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-brand-border bg-white p-8">
          <div className="overline mb-4">Next steps</div>
          <div className="font-display text-lg text-brand-navy font-semibold">Ready when you are.</div>
          <p className="mt-2 text-brand-muted text-sm">Every in-stock product ships signature-required with lot-tracked cartons. Add items to your cart and check out securely with Stripe.</p>
          <Link to="/shop" data-testid="dashboard-shop-cta" className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-brand-accent">Browse catalog <ArrowRight size={12} weight="bold" /></Link>
        </div>

        <div className="border border-brand-border bg-white p-8">
          <div className="overline mb-4 flex items-center gap-2"><Headset size={14} weight="duotone" className="text-brand-accent" /> Customer support</div>
          <div className="font-display text-lg text-brand-navy font-semibold">Lot documentation on request</div>
          <p className="mt-2 text-brand-muted text-sm">Customers can request lot-specific documentation for shipped orders directly from our support team.</p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-brand-accent" data-testid="dashboard-contact-cta">
            Contact support <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>

      <div className="mt-12">
        <div className="overline mb-4">Recent orders</div>
        {orders.length === 0 ? (
          <div className="border border-brand-border p-10 text-sm text-brand-muted bg-white" data-testid="orders-empty">
            No orders yet. Your order history will appear here after your first order.
          </div>
        ) : (
          <div className="border border-brand-border divide-y divide-brand-border">
            {orders.map((o) => (
              <div
                key={o.id}
                className="px-6 py-4 text-sm bg-white"
                data-testid={`order-row-${o.id}`}
              >
                <div className="grid grid-cols-12 items-center gap-4">
                  <div className="col-span-4 font-mono text-brand-navy text-xs">{o.id.slice(0, 8)}</div>
                  <div className="col-span-4 text-brand-muted text-xs">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="col-span-2 text-brand-navy font-mono">{formatPrice(o.total_cents)}</div>
                  <div className="col-span-2 text-right"><StatusBadge status={o.status} kind="compliance" /></div>
                </div>
                {(o.tracking_number || o.carrier) && (
                  <div
                    className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-brand-muted font-mono"
                    data-testid={`order-tracking-${o.id}`}
                  >
                    <Truck size={12} weight="duotone" className="text-brand-accent" />
                    <span>{o.carrier || "Carrier pending"}</span>
                    {o.tracking_number && (
                      o.tracking_url ? (
                        <a
                          href={o.tracking_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-accent hover:underline"
                          data-testid={`order-tracking-link-${o.id}`}
                        >
                          {o.tracking_number}
                        </a>
                      ) : (
                        <span>{o.tracking_number}</span>
                      )
                    )}
                    {o.estimated_delivery_start && o.estimated_delivery_end && (
                      <span className="text-brand-muted">
                        · ETA {o.estimated_delivery_start} → {o.estimated_delivery_end}
                      </span>
                    )}
                    {o.fulfillment_email_status?.customer === "sent" && (
                      <span className="text-emerald-600" data-testid={`order-shipnotif-${o.id}`}>
                        · Ship notice emailed
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
